import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Row,
  Col,
  Typography,
  Space,
  Empty,
  Spin,
  Popconfirm,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

interface Dataset {
  id: number;
  name: string;
  buckets: string[];
  type: string;
  image_count?: number;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const DatasetList: React.FC = () => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    try {
      const response = await axios.get(`${API_URL}/datasets`);
      setDatasets(response.data);
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
      message.error('Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/datasets/${id}`);
      setDatasets(datasets.filter(d => d.id !== id));
      message.success('Dataset deleted successfully');
    } catch (error) {
      console.error('Failed to delete dataset:', error);
      message.error('Failed to delete dataset');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          <DatabaseOutlined style={{ marginRight: '12px', color: '#6366f1' }} />
          Datasets
        </Title>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate('/datasets/create')}
        >
          Create Dataset
        </Button>
      </div>

      {datasets.length === 0 ? (
        <Card>
          <Empty
            image={<FolderOpenOutlined style={{ fontSize: '64px', color: '#ccc' }} />}
            description={
              <Space direction="vertical" size="small">
                <Text strong style={{ fontSize: '16px' }}>No datasets yet</Text>
                <Text type="secondary">Create your first dataset to organize your images by buckets.</Text>
              </Space>
            }
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/datasets/create')}>
              Create Dataset
            </Button>
          </Empty>
        </Card>
      ) : (
        <Row gutter={[20, 20]}>
          {datasets.map(dataset => (
            <Col xs={24} sm={12} lg={8} xl={6} key={dataset.id}>
              <Card
                hoverable
                onClick={() => navigate(`/datasets/${dataset.id}`)}
                style={{ height: '100%' }}
                actions={[
                  <Popconfirm
                    title="Delete dataset"
                    description="Are you sure you want to delete this dataset?"
                    onConfirm={(e) => handleDelete(e as any, dataset.id)}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                ]}
              >
                <Card.Meta
                  title={
                    <Space>
                      <DatabaseOutlined style={{ color: '#6366f1' }} />
                      {dataset.name}
                      <Tag color={
                        dataset.type === 'Collection' ? 'gold'
                          : dataset.type === 'Anime' ? 'purple'
                          : 'green'
                      }>
                        {dataset.type || 'Photo'}
                      </Tag>
                    </Space>
                  }
                  description={
                    dataset.type === 'Collection' ? (
                      <div style={{ marginTop: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {dataset.image_count ?? 0} collected image(s)
                        </Text>
                      </div>
                    ) : (
                      <div style={{ marginTop: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                          {dataset.buckets.length} bucket(s)
                        </Text>
                        <Space size={[4, 8]} wrap>
                          {dataset.buckets.map(bucket => (
                            <Tag key={bucket} color="blue">{bucket}</Tag>
                          ))}
                        </Space>
                      </div>
                    )
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default DatasetList;
